import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import '../assets/css/Hero.css';
import '../assets/css/InsightPreviewer.css';
import heroVideo from '../assets/vid/Comparedly-Demo.mp4';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';
import igIcon from '../assets/img/platform/instagram.png';
import fbIcon from '../assets/img/platform/facebook.png';
import ytIcon from '../assets/img/platform/youtube.png';
import fbVerifiedIcon from '../assets/img/platform/facebook-verified.png';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const appFrontendUrl = process.env.REACT_APP_URL || 'https://app.comparedly.com';


const useCyclingPlaceholder = (list, intervalMs = 3000) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % list.length), intervalMs);
    return () => clearInterval(t);
  }, [list, intervalMs]);
  return list[idx];
};

const formatFollowers = (count) => {
  if (!count) return '0';
  if (count >= 1000000) return (count / 1000000).toFixed(1) + 'M';
  if (count >= 1000) return (count / 1000).toFixed(1) + 'K';
  return count;
};

const SearchProgress = ({ progress }) => {
  return (
    <div style={{ position: 'relative', width: '50px', height: '50px', margin: '0 auto' }}>
      <div className="search-spinner" style={{ width: '50px', height: '50px', borderWidth: '4px' }}></div>
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        fontSize: '0.75rem',
        fontWeight: '700',
        color: '#00A1A1',
        fontFamily: "'Poppins', sans-serif"
      }}>
        {progress || 0}%
      </div>
    </div>
  );
};

const ResultDropdown = ({ results, onSelect, type, isSearching, isPreSearching, hasError, isRateLimited, onRetry, searchProgress }) => {
  if (isRateLimited) return (
    <div className="search-dropdown">
      <div className="search-loading-container limit-state" style={{
        textAlign: 'center',
        padding: '32px 20px',
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          color: '#1a202c',
          marginBottom: '12px',
          fontWeight: '800',
          fontSize: '1.2rem',
          fontFamily: "'Poppins', sans-serif"
        }}>Limit Reached</div>
        <p style={{
          fontSize: '0.95rem',
          color: '#4a5568',
          marginBottom: '24px',
          lineHeight: '1.6',
          fontFamily: "'Lato', sans-serif"
        }}>
          You have used your allowance. Please Sign up for a free account to continue exploring Unlimited insights
        </p>
        <button
          className="btn-primary btn-glitter"
          onClick={() => window.location.href = `${appFrontendUrl}/auth/register`}
          style={{ padding: '14px 32px', fontSize: '1rem', width: 'auto', margin: '0 auto', display: 'block' }}
        >
          Start Your Free Trial
        </button>
        {/* Trust Signals */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '12px',
          marginTop: '20px',
          fontSize: '0.75rem',
          color: '#718096',
          flexWrap: 'wrap',
          fontFamily: "'Lato', sans-serif"
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <CheckCircleIcon sx={{ fontSize: 16, color: '#00A1A1' }} />
            <span>No card required</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <CheckCircleIcon sx={{ fontSize: 16, color: '#00A1A1' }} />
            <span>Cancel anytime</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <CheckCircleIcon sx={{ fontSize: 16, color: '#00A1A1' }} />
            <span>No contract</span>
          </div>
        </div>
      </div>
    </div>
  );
  if (hasError) return (
    <div className="search-dropdown">
      <div className="search-loading-container error-state">
        <div style={{ color: '#d9534f', marginBottom: '8px', fontWeight: '600' }}>Search failed</div>
        <button className="search-retry-btn" onClick={(e) => { e.stopPropagation(); onRetry(); }}>Retry</button>
      </div>
    </div>
  );
  if (isSearching || isPreSearching) return (
    <div className="search-dropdown">
      <div className="search-loading-container" style={{ padding: '30px 0' }}>
        <SearchProgress progress={searchProgress} />
      </div>
    </div>
  );
  if (!results || results.length === 0) return null;
  return (
    <div className="search-dropdown" onWheel={(e) => e.stopPropagation()} onTouchMove={(e) => e.stopPropagation()}>
      {results.map((item, idx) => (
        <div key={idx} className="search-result-item" onClick={() => onSelect(item, type)}>
          <div className="avatar-wrapper" style={{ position: 'relative' }}>
            {item.picture ? (
              <img src={item.picture} className="result-avatar" alt={item.name} onError={(e) => { e.target.style.display = 'none'; }} />
            ) : null}
            <div className="result-avatar-placeholder">{item.name?.charAt(0)}</div>
            {item.platform === 'Facebook' && (item.verification_status === 'verified' || item.verification_status === 'blue_verified') && (
              <img src={fbVerifiedIcon} className="verified-badge-sm" alt="Verified" />
            )}
          </div>
          <div className="result-info">
            <div className="result-name">{item.name}</div>
            <div className="result-meta">
              <img
                src={item.platform === 'Instagram' ? igIcon : item.platform === 'Facebook' ? fbIcon : ytIcon}
                className="platform-icon-sm"
                style={{ marginRight: '6px' }}
                alt={item.platform}
              />
              <span>{formatFollowers(item.followers)} {item.platform === "YouTube" ? "Subscribers" : "Followers"}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const textVariants = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: 'easeOut' } }
};
const imageVariants = {
  hidden: { opacity: 0, x: 50, scale: 0.95 },
  visible: { opacity: 1, x: 0, scale: 1, transition: { duration: 0.8, ease: 'easeOut', delay: 0.2 } }
};
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.2, delayChildren: 0.1 } }
};

const Hero = ({ onScoutResult, clientId, searchProgress }) => {
  const [primaryHandle, setPrimaryHandle] = useState('');
  const [competitorHandle, setCompetitorHandle] = useState('');
  const [primaryId, setPrimaryId] = useState('');
  const [competitorId, setCompetitorId] = useState('');
  const [primaryPlatform, setPrimaryPlatform] = useState('Instagram');
  const [competitorPlatform, setCompetitorPlatform] = useState('Instagram');

  const [primaryResults, setPrimaryResults] = useState([]);
  const [competitorResults, setCompetitorResults] = useState([]);
  const [showPrimaryDropdown, setShowPrimaryDropdown] = useState(false);
  const [showCompetitorDropdown, setShowCompetitorDropdown] = useState(false);
  const [isSearchingPrimary, setIsSearchingPrimary] = useState(false);
  const [isSearchingCompetitor, setIsSearchingCompetitor] = useState(false);
  const [searchErrorPrimary, setSearchErrorPrimary] = useState(false);
  const [searchErrorCompetitor, setSearchErrorCompetitor] = useState(false);
  const [isRateLimitedPrimary, setIsRateLimitedPrimary] = useState(false);
  const [isRateLimitedCompetitor, setIsRateLimitedCompetitor] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const isSelectingRef = React.useRef({ primary: false, competitor: false });
  const selectedRef = React.useRef({
    primaryId: "", primaryPlatform: "",
    competitorId: "", competitorPlatform: ""
  });

  const fetchResults = async (query, type) => {
    if (type === 'primary') { setIsSearchingPrimary(true); setSearchErrorPrimary(false); }
    else { setIsSearchingCompetitor(true); setSearchErrorCompetitor(false); }

    if (onScoutResult) {
      onScoutResult(prev => ({ ...prev, progress: 5 }));
    }

    try {
      const resp = await axios.get(`${API_URL}/preview/search?q=${encodeURIComponent(query)}&clientId=${clientId}`);
      if (type === 'primary') {
        const pIdStr = String(selectedRef.current.competitorId).toLowerCase();
        const pPlatStr = String(selectedRef.current.competitorPlatform).toLowerCase();
        const filtered = (resp.data.data || []).filter(item => {
          return !(String(item.id).toLowerCase() === pIdStr && String(item.platform).toLowerCase() === pPlatStr);
        });
        setPrimaryResults(filtered);
        setShowPrimaryDropdown(true);
      } else {
        const pIdStr = String(selectedRef.current.primaryId).toLowerCase();
        const pPlatStr = String(selectedRef.current.primaryPlatform).toLowerCase();
        const filtered = (resp.data.data || []).filter(item => {
          return !(String(item.id).toLowerCase() === pIdStr && String(item.platform).toLowerCase() === pPlatStr);
        });
        setCompetitorResults(filtered);
        setShowCompetitorDropdown(true);
      }
    } catch (err) {
      if (err.response?.status === 429) {
        if (type === 'primary') setIsRateLimitedPrimary(true);
        else setIsRateLimitedCompetitor(true);
      } else {
        if (type === 'primary') setSearchErrorPrimary(true);
        else setSearchErrorCompetitor(true);
      }
    } finally {
      setIsSearchingPrimary(false);
      setIsSearchingCompetitor(false);
    }
  };

  useEffect(() => {
    if (primaryHandle.length < 2) { setPrimaryResults([]); setShowPrimaryDropdown(false); return; }
    if (isSelectingRef.current.primary) { isSelectingRef.current.primary = false; return; }
    const timer = setTimeout(() => fetchResults(primaryHandle, 'primary'), 1000);
    return () => clearTimeout(timer);
  }, [primaryHandle, clientId]);

  useEffect(() => {
    if (competitorHandle.length < 2) { setCompetitorResults([]); setShowCompetitorDropdown(false); return; }
    if (isSelectingRef.current.competitor) { isSelectingRef.current.competitor = false; return; }
    const timer = setTimeout(() => fetchResults(competitorHandle, 'competitor'), 1000);
    return () => clearTimeout(timer);
  }, [competitorHandle, clientId]);

  const handleSelect = (item, type) => {
    // 📝 Log selection for audience analysis (Asynchronous)
    axios.post(`${API_URL}/preview/log-selection`, {
      clientId,
      selection: {
        id: item.id,
        name: item.name,
        platform: item.platform,
        selectionType: type
      }
    }).catch(e => console.warn('Failed to log selection', e));

    if (type === 'primary') {
      isSelectingRef.current.primary = true;
      selectedRef.current.primaryId = item.id;
      selectedRef.current.primaryPlatform = item.platform;
      
      setPrimaryHandle(item.name); setPrimaryId(item.id); setPrimaryPlatform(item.platform);
      setShowPrimaryDropdown(false); setPrimaryResults([]);
    } else {
      isSelectingRef.current.competitor = true;
      selectedRef.current.competitorId = item.id;
      selectedRef.current.competitorPlatform = item.platform;
      
      setCompetitorHandle(item.name); setCompetitorId(item.id); setCompetitorPlatform(item.platform);
      setShowCompetitorDropdown(false); setCompetitorResults([]);
    }
  };

  const handleScout = async () => {
    if (!primaryHandle || !competitorHandle) { setError('Please enter both brands to begin.'); return; }
    setLoading(true); setError('');
    if (onScoutResult) onScoutResult({ loading: true, datasets: [], insights: [] });

    // Scroll down smoothly to results section
    setTimeout(() => {
      const resultsEl = document.getElementById('scout-results');
      if (resultsEl) resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200);

    try {
      const response = await axios.post(`${API_URL}/preview/scout`, {
        primaryId: primaryId || primaryHandle,
        competitorId: competitorId || competitorHandle,
        primaryName: primaryHandle,
        competitorName: competitorHandle,
        platform: primaryPlatform,
        primaryPlatform, competitorPlatform,
        clientId,
      });
      if (response.data.success) {
        const { generateStrategicNarrative } = await import('../utils/insightEngine');
        const insights = generateStrategicNarrative(response.data.datasets);
        if (onScoutResult) onScoutResult({ loading: false, datasets: response.data.datasets, insights });
      } else {
        const msg = response.data.message || 'Could not generate insights.';
        setError(msg);
        if (onScoutResult) onScoutResult({ loading: false, datasets: [], insights: [], error: msg });
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Something went wrong. Please try again.';
      setError(msg);
      if (onScoutResult) onScoutResult({ loading: false, datasets: [], insights: [], error: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="hero-section">
      <motion.div
        className="hero-container"
        style={{ flexDirection: 'column', alignItems: 'stretch', gap: 0 }}
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* ── Full-width headline + subtext ── */}
        <motion.div variants={textVariants} style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h1 className="hero-title" style={{ margin: '0 auto' }}>
            Why is my competitor growing<br />
            <span style={{ color: '#FFB347' }}>3.5X faster</span> than me?
          </h1>
          <p className="hero-description" style={{ marginTop: '16px', maxWidth: '700px', margin: '16px auto 0' }}>
            Instant Cross Platform Competitor Analysis.<br />
            <span style={{ color: '#00A1A1', fontWeight: '700' }}>The Only platform that tells you what to do next,</span> not just how many likes they got.
          </p>
        </motion.div>

        {/* ── Two-column row: form left, video right ── */}
        <div className="hero-content-split">

          {/* Left: Scout form with soft rounded container */}
          <motion.div
            variants={textVariants}
            style={{
              flex: 1,
              background: '#f8fdfd', // Light teal tint to match brand style
              padding: '24px',
              borderRadius: '20px', // Match video radius
              border: '1px solid #e2e8f0',
              boxShadow: '0 20px 40px rgba(0, 161, 161, 0.2)', // Same shadow as video
              display: 'flex',
              flexDirection: 'column',
              gap: 0
            }}
          >
            <h3 style={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: '1.25rem',
              fontWeight: '800',
              color: '#2d3748',
              marginBottom: '24px',
              textAlign: 'left',
              letterSpacing: '-0.02em',
              marginTop: 0
            }}>Try Instant Insights</h3>
            <div className="insight-inputs" style={{ marginBottom: '16px' }}>
              <div className="input-group">
                <label className="input-label">Your Brand</label>
                <input
                  className="brand-input"
                  placeholder="Type brand/handle or URL..."
                  value={primaryHandle}
                  onChange={(e) => {
                    setPrimaryHandle(e.target.value);
                    setSearchErrorPrimary(false);
                    setIsRateLimitedPrimary(false);
                    if (e.target.value.length >= 2) { 
                      setIsSearchingPrimary(true); 
                      setPrimaryResults([]); 
                      setShowPrimaryDropdown(true); 
                    }
                    else { 
                      setIsSearchingPrimary(false); 
                      setShowPrimaryDropdown(false); 
                    }
                  }}
                  onBlur={() => setTimeout(() => setShowPrimaryDropdown(false), 200)}
                />
                {showPrimaryDropdown && (
                  <div onWheel={(e) => e.stopPropagation()}>
                    <ResultDropdown results={primaryResults} onSelect={handleSelect} type="primary"
                      isSearching={isSearchingPrimary} 
                      hasError={searchErrorPrimary} isRateLimited={isRateLimitedPrimary}
                      onRetry={() => fetchResults(primaryHandle, 'primary')} 
                      searchProgress={searchProgress} />
                  </div>
                )}
              </div>
              <div className="input-group">
                <label className="input-label">Competitor</label>
                <input
                  className="brand-input"
                  placeholder="Type brand/handle or URL..."
                  value={competitorHandle}
                  onChange={(e) => {
                    setCompetitorHandle(e.target.value);
                    setSearchErrorCompetitor(false);
                    setIsRateLimitedCompetitor(false);
                    if (e.target.value.length >= 2) { 
                      setIsSearchingCompetitor(true); 
                      setCompetitorResults([]); 
                      setShowCompetitorDropdown(true); 
                    }
                    else { 
                      setIsSearchingCompetitor(false); 
                      setShowCompetitorDropdown(false); 
                    }
                  }}
                  onBlur={() => setTimeout(() => setShowCompetitorDropdown(false), 200)}
                />
                {showCompetitorDropdown && (
                  <div onWheel={(e) => e.stopPropagation()}>
                    <ResultDropdown results={competitorResults} onSelect={handleSelect} type="competitor"
                      isSearching={isSearchingCompetitor} 
                      hasError={searchErrorCompetitor} isRateLimited={isRateLimitedCompetitor}
                      onRetry={() => fetchResults(competitorHandle, 'competitor')} 
                      searchProgress={searchProgress} />
                  </div>
                )}
              </div>
            </div>

            <button className="scout-button" onClick={handleScout} disabled={loading} style={{ width: 'fit-content', padding: '14px 32px' }}>
              {loading ? 'Analyzing...' : 'Get My Strategy Preview — Free'}
            </button>

            {error && <p style={{ color: '#ef4444', marginTop: '12px', fontWeight: '700', fontSize: '0.95rem', fontFamily: "'Poppins', sans-serif" }}>{error}</p>}

            {/* Benefits */}
            <div className="hero-benefits" style={{ marginTop: '20px' }}>
              <div className="benefit-item"><CheckCircleIcon className="benefit-icon" /><span>No Sign Up Required</span></div>
              <div className="benefit-item"><CheckCircleIcon className="benefit-icon" /><span>Real Public Data</span></div>
              <div className="benefit-item"><CheckCircleIcon className="benefit-icon" /><span>Instant Access</span></div>
            </div>
          </motion.div>

          {/* Right: Demo video - restored to original */}
          <motion.div variants={imageVariants} style={{ flex: 1 }}>
            <video src={heroVideo} className="responsive-image" autoPlay loop muted playsInline />
          </motion.div>
        </div>

      </motion.div>
    </section>
  );
};

export default Hero;
